import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Input } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useStocksServiceConfirmEntryEndpoint,
  useStocksServiceGetEntryEndpoint,
  useStocksServiceGetEntryEndpointKey,
  useStocksServiceRejectEntryEndpoint
} from 'src/api/queries'
import {
  ApiError,
  InventoryDocumentPartItemResponse,
  InventoryDocumentState
} from 'src/api/requests'
import {
  ConfirmModal,
  LoadingScreen,
  message,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import { InputConfirmedDetails } from 'src/features/stock/components'
import { useFormStyles } from 'src/features/stock/styles'
import { useScrollToFirstError, ValidationError } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

export const StockInputsDetailsPage = () => {
  const { id } = useParams()

  const [isReject, setIsReject] = useState(false)

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { styles } = useFormStyles()
  const { onScrollToError } = useScrollToFirstError(form)

  const navigate = useNavigate()
  const { t, currentLang } = useLocales()

  const printableRef = useRef<HTMLDivElement>(null)

  const getUnitPrice = (item: InventoryDocumentPartItemResponse) => {
    if (data?.contract?.isInternal) {
      return item?.finalUnitPrice && item?.finalUnitPrice > 0
        ? item?.finalUnitPrice
        : item?.unitPrice
    }
    return item?.unitPrice
  }

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Entry_${id}`
  })

  const { data, isLoading, refetch } = useStocksServiceGetEntryEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetEntryEndpointKey, { id: String(id) }]
    })
    refetch()
  }

  const { mutate: acceptMutation } = useStocksServiceConfirmEntryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('stock_administration.accepted_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: rejectMutation } = useStocksServiceRejectEntryEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('stock_administration.rejected_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.inputs.root)
  }

  const isNew =
    data?.state === InventoryDocumentState.NEW ||
    data?.state === InventoryDocumentState.WAITING

  useEffect(() => {
    if (id && data) {
      const existingFileIds = data?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      form.setFieldsValue({
        invoiceDate: toDayjs(data?.documentDate),
        invoiceNumber: data?.documentNumber,
        contractId: `${data?.beneficiary?.name} (${t('organizational_structure.idno')}: ${data?.beneficiary?.idno}, ${t('counterparts.contract_number')}: ${data?.contract?.id})`,
        supplier: data?.supplier?.name,
        purchase_center: data?.beneficiary?.name,
        takeoverDate: data?.takeoverDate
          ? dayjs(data?.takeoverDate)
          : undefined,
        deliveryDate: data?.deliveryDate
          ? dayjs(data?.deliveryDate)
          : undefined,
        transporterId: data?.transporter?.id ?? '-',
        transporterAccompanyingNotice: data?.accompanyingNotice ?? '-',
        medicine: data?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.product?.name} ${item?.doses}`,
          productName: item?.producer?.name,
          producerName: item?.product?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          productionDate: toDayjs(item?.productionDate)?.format(
            yearMonthDaySlash
          ),
          unitPrice: getUnitPrice(item)?.toFixed(4),
          priceTotal: (
            ((item?.unitPrice ?? 0) + (item?.unitPriceTva ?? 0)) *
            (item?.quantity ?? 0)
          ).toFixed(4),

          tvaUnitRateName: item?.tvaRate?.name?.[currentLang.value],
          tvaUnitRateId: item?.tvaRate?.id,
          quantity: item?.quantity
        })),
        consumables: data?.items?.map((item) => ({
          barCode: item?.barCode,
          name: `${item?.product?.name} ${item?.doses}`,
          productName: item?.producer?.name,
          producerName: item?.product?.name,
          expirationDate: toDayjs(item?.expirationDate)?.format(
            yearMonthDaySlash
          ),
          productionDate: toDayjs(item?.productionDate)?.format(
            yearMonthDaySlash
          ),
          unitPrice: getUnitPrice(item)?.toFixed(4),
          priceTotal: (
            ((item?.unitPrice ?? 0) + (item?.unitPriceTva ?? 0)) *
            (item?.quantity ?? 0)
          ).toFixed(4),
          tvaUnitRateName: item?.tvaRate?.name?.[currentLang.value],
          tvaUnitRateId: item?.tvaRate?.id,
          quantity: item?.quantity
        })),
        services: data?.services?.map((service) => ({
          serviceName: service.service?.translation?.[currentLang.value],
          unitPrice: service.unitPrice
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        })),
        comment: data?.comment
      })
    }
  }, [data, id])

  const onAccept = async () => {
    try {
      const values = await form.validateFields()

      acceptMutation({
        id: String(data?.id),
        requestBody: {
          deliveryDate: dayjs(values?.deliveryDate).format(yearMonthDaySlash),
          comment: values.commentNew
        }
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  const onReject = async () => {
    form.setFieldsValue({
      deliveryDate: dayjs(),
      comment: form.getFieldValue('commentReject') ?? ' '
    })

    const values = await form.validateFields()

    rejectMutation({
      id: String(data?.id),
      requestBody: {
        comment: values.commentNew
      }
    })
    setIsReject(false)
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={`${t('stock.stock_entry')}: ${String(data?.code)}`}
      items={[
        {
          title: t('menu.consumable_medicaments'),
          path: paths.inputs.root
        },
        {
          title: t('stock.inputs'),
          path: paths.inputs.root
        },
        {
          title: `${t('stock.stock_entry')}: ${String(data?.code)}`
        }
      ]}
    >
      <Flex vertical gap={24} className={styles.form}>
        <Flex justify="space-between">
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>

          <Flex gap={16}>
            {!data && (
              <Button onClick={onBackClick}>{t('common.cancel')}</Button>
            )}

            <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
              {t('stock_applications.print')}
            </Button>

            {isNew && (
              <>
                <SaveConfirm
                  title={`${t('stock_administration.accept')}?`}
                  placement="leftBottom"
                  onConfirm={onAccept}
                >
                  <Button type="primary">
                    {t('stock_administration.accept')}
                  </Button>
                </SaveConfirm>

                <SaveConfirm
                  title={`${t('stock_administration.reject')}?`}
                  placement="leftBottom"
                  onConfirm={() => setIsReject(true)}
                >
                  <Button danger>{t('stock_administration.reject')}</Button>
                </SaveConfirm>
              </>
            )}
          </Flex>
        </Flex>

        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          disabled={!!data && !isReject}
          scrollToFirstError
        >
          <div ref={printableRef}>
            <InputConfirmedDetails item={data} form={form} />
          </div>

          <ConfirmModal
            open={isReject}
            title={`${t('stock_administration.reject')}?`}
            onCancel={() => setIsReject(false)}
            onOk={onReject}
            okButtonProps={{ danger: true }}
          >
            <Card>
              <Form.Item name="commentNew">
                <Input.TextArea
                  rows={5}
                  placeholder={t('common.leave_comment')}
                />
              </Form.Item>
            </Card>
          </ConfirmModal>
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
